/** @jsxImportSource @emotion/react */
import { formatDate } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';

import { presentUser } from '@kaiizen/kai-presenters';
import { type ApiActivityLog } from '@kaiizen/kai-pro-api';
import { useAuth, useUsers } from '@kaiizen/kai-pro-api-react-state';

import { CaretDownIcon } from '@/Client/Icons/kaiShikiIconAliases';
import type { ProjectLifecycleStateType } from '@/Client/Layout';
import { useProjectActivityLog } from '@/Client/State';
import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  styleguideBorderWidth,
  styleguideElevation,
  styleguideFontFamily,
  styleguideLetterSpacing,
  styleguideRadius,
  styleguideSpacing,
  withOpacity,
} from '@kaiizen/kai-shiki/core';
import { CheckIcon, LockIcon } from '@kaiizen/kai-shiki/react';

const HISTORY_LIMIT = 2;
const ACTIVITY_LOG_LIMIT = 100;
const ON_INK_TEXT = withOpacity(styleguideColors.white, 0.78);
const ON_INK_FILL = withOpacity(styleguideColors.white, 0.14);

const ROOT_STYLE = css`
  label: TURN_RESPONSIBILITY_CHIP_ROOT_STYLE;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: ${styleguideSpacing.s2}px;
  flex-shrink: 0;
`;

const LOCK_STYLE = css`
  label: TURN_RESPONSIBILITY_CHIP_LOCK_STYLE;
  color: ${ON_INK_TEXT};
  flex-shrink: 0;
`;

const BUTTON_STYLE = (open: boolean) =>
  css`
  label: TURN_RESPONSIBILITY_CHIP_BUTTON_STYLE;
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0;
  overflow: hidden;
  background: ${open ? styleguideColors.white : ON_INK_FILL};
  border: 0;
  /* The collaboration prototype uses a true capsule here, not a rounded
     rectangle: the radius must always exceed this 22px control's height. */
  border-radius: 999px;
  box-shadow: ${open ? `0 0 0 2px ${withOpacity(styleguideColors.accent, 0.55)}` : 'none'};
  color: ${open ? styleguideColors.ink : ON_INK_TEXT};
  cursor: pointer;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(9)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;

  &:hover {
    background: ${open ? styleguideColors.white : withOpacity(styleguideColors.white, 0.26)};
    color: ${open ? styleguideColors.ink : styleguideColors.white};
  }

  &:focus-visible {
    outline: 2px solid ${styleguideColors.accent};
    outline-offset: 2px;
  }
`;

const LABEL_STYLE = css`
  label: TURN_RESPONSIBILITY_CHIP_LABEL_STYLE;
  padding: 0 ${styleguideSpacing.s2}px 0 ${styleguideSpacing.s3}px;
`;

const CARET_STYLE = (open: boolean) =>
  css`
  label: TURN_RESPONSIBILITY_CHIP_CARET_STYLE;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding: 0 6px;
  background: ${open ? withOpacity(styleguideColors.ink, 0.1) : 'rgba(0, 0, 0, 0.16)'};
  box-shadow: inset 1px 0 0 0 ${open ? withOpacity(styleguideColors.ink, 0.14) : withOpacity(styleguideColors.white, 0.18)};

  & svg {
    /* Kai Shiki's caret glyph points right; orient it down when closed and up
       when the responsibility card is open, as in the collaboration prototype. */
    transform: ${open ? 'rotate(-90deg)' : 'rotate(90deg)'};
    transition: transform 0.16s;
  }
`;

const POPOVER_STYLE = css`
  label: TURN_RESPONSIBILITY_POPOVER_STYLE;
  position: absolute;
  top: calc(100% + ${styleguideSpacing.s2}px);
  left: 0;
  z-index: 20;
  width: 280px;
  box-sizing: border-box;
  padding: ${styleguideSpacing.s3}px;
  background: ${styleguideColors.white};
  border: ${styleguideBorderWidth.thin}px solid ${styleguideColors.stroke};
  box-shadow: ${styleguideElevation.modal};
  color: ${styleguideColors.ink};
`;

const STATE_STYLE = css`
  label: TURN_RESPONSIBILITY_STATE_STYLE;
  display: flex;
  align-items: center;
  gap: ${styleguideSpacing.s2}px;
  margin: 0 0 ${styleguideSpacing.s3}px;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.monoCaption)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoCaption};
  text-transform: uppercase;
`;

const DETAIL_LIST_STYLE = css`
  label: TURN_RESPONSIBILITY_DETAIL_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${styleguideSpacing.s2}px;
  margin: 0;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodySmall)};
  line-height: 1.45;
`;

const DETAIL_ROW_STYLE = css`
  label: TURN_RESPONSIBILITY_DETAIL_ROW_STYLE;
  margin: 0;
  color: ${styleguideColors.ink};
`;

const DETAIL_TERM_STYLE = css`
  label: TURN_RESPONSIBILITY_DETAIL_TERM_STYLE;
  margin-right: ${styleguideSpacing.s1}px;
  font-family: ${styleguideFontFamily.body};
  font-size: inherit;
  font-weight: ${styleguideFontWeight.bold};
`;

const HISTORY_STYLE = css`
  label: TURN_RESPONSIBILITY_HISTORY_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${styleguideSpacing.s2}px;
  margin: ${styleguideSpacing.s3}px 0 0;
  padding: ${styleguideSpacing.s3}px 0 0;
  border-top: ${styleguideBorderWidth.thin}px solid ${styleguideColors.stroke};
  list-style: none;
`;

const HISTORY_ENTRY_STYLE = css`
  label: TURN_RESPONSIBILITY_HISTORY_ENTRY_STYLE;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  column-gap: ${styleguideSpacing.s2}px;
`;

const HISTORY_ICON_STYLE = css`
  label: TURN_RESPONSIBILITY_HISTORY_ICON_STYLE;
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  border-radius: ${styleguideRadius.circle};
  background: ${styleguideColors.ink};
  color: ${styleguideColors.white};
`;

const HISTORY_COPY_STYLE = css`
  label: TURN_RESPONSIBILITY_HISTORY_COPY_STYLE;
  min-width: 0;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodySmall)};
  line-height: 1.35;
`;

const HISTORY_DATE_STYLE = css`
  label: TURN_RESPONSIBILITY_HISTORY_DATE_STYLE;
  display: block;
  margin-bottom: 1px;
  color: ${styleguideColors.midtone};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.monoCaption)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoCaption};
  text-transform: uppercase;
`;

const isResponsibilityHistoryEntry = (entry: ApiActivityLog) =>
  entry.eventType === 'projectCreated'
  || entry.eventType === 'projectHandedOff'
  || entry.eventType === 'projectWorkTaken';

const getPersonName = (name?: string, userId?: string) => name?.trim() || userId || 'Unknown person';

const getCompactPersonName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name;
  return `${parts[0]![0]}. ${parts.at(-1)}`;
};

const getStageLabel = (phase: ProjectLifecycleStateType['phase']) => {
  if (phase === 'review') return 'In review';
  if (phase === 'closeout') return 'Closeout';
  return 'Draft';
};

const getHistoryCopy = (entry: ApiActivityLog) => {
  const actorName = getPersonName(entry.actorName, entry.actorUserId);
  if (entry.eventType === 'projectCreated') return `${actorName} created the project`;
  if (entry.eventType === 'projectWorkTaken') return `${actorName} took responsibility for the project`;
  return `${actorName} handed off the project to ${getPersonName(entry.aboutUserName, entry.aboutUserId)}`;
};

const compareMostRecentFirst = (left: ApiActivityLog, right: ApiActivityLog) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();

const isAssignmentEventForUser = (entry: ApiActivityLog, userId: string) =>
  (entry.eventType === 'projectHandedOff' && entry.aboutUserId === userId)
  || (entry.eventType === 'projectWorkTaken' && entry.actorUserId === userId);

const getRelativeAssignmentTime = (assignedAt?: Date | string) => {
  if (!assignedAt) return undefined;

  const now = new Date();
  const assignmentDate = new Date(assignedAt);
  now.setHours(0, 0, 0, 0);
  assignmentDate.setHours(0, 0, 0, 0);
  const elapsedDays = Math.max(0, Math.round((now.getTime() - assignmentDate.getTime()) / (24 * 60 * 60 * 1000)));

  if (elapsedDays === 0) return 'today';
  if (elapsedDays === 1) return 'yesterday';
  if (elapsedDays < 7) return `${elapsedDays} days ago`;
  return `on ${formatDate(assignedAt, 'MMM d, yyyy')}`;
};

const getWhenCopy = ({
  artifactType,
  assignmentEvent,
  assignedAt,
  isCurrentHolder,
  responsibleName,
}: {
  artifactType: string;
  assignmentEvent?: ApiActivityLog;
  assignedAt?: Date | string;
  isCurrentHolder: boolean;
  responsibleName: string;
}) => {
  const subject = isCurrentHolder ? 'You' : responsibleName;
  const relativeTime = getRelativeAssignmentTime(assignmentEvent?.createdAt ?? assignedAt);
  const timeSuffix = relativeTime ? ` ${relativeTime}` : '';

  if (assignmentEvent?.eventType === 'projectHandedOff') {
    return `${subject} received this ${artifactType} from ${getPersonName(assignmentEvent.actorName, assignmentEvent.actorUserId)}${timeSuffix}.`;
  }
  if (assignmentEvent?.eventType === 'projectWorkTaken') {
    const previousHolder = getPersonName(assignmentEvent.aboutUserName, assignmentEvent.aboutUserId);
    return `${subject} took responsibility for this ${artifactType}${assignmentEvent.aboutUserId ? ` from ${previousHolder}` : ''}${timeSuffix}.`;
  }
  return `${subject} became responsible for this ${artifactType}${timeSuffix}.`;
};

const getNextCopy = ({ artifactType, canApprove, isCurrentHolder, responsibleName }: {
  artifactType: string;
  canApprove: boolean;
  isCurrentHolder: boolean;
  responsibleName: string;
}) => {
  const holder = isCurrentHolder ? 'you' : responsibleName;
  const nextAction = `Then, ${holder} can hand off to the next person to continue building this ${artifactType}`;
  return `Only ${holder} can edit it currently. ${
    canApprove ? `${nextAction} or give final approval.` : `${nextAction}. A Project Manager can give final approval.`
  }`;
};

export const TurnResponsibilityChip = ({
  artifactType,
  canResponsibleUserApprove,
  projectId,
  responsibleUserId,
  responsibleUserAssignedAt,
  phase,
  measurement = false,
}: {
  artifactType: string;
  canResponsibleUserApprove: boolean;
  projectId: string;
  responsibleUserId: string;
  responsibleUserAssignedAt?: Date | string;
  phase: ProjectLifecycleStateType['phase'];
  measurement?: boolean;
}) => {
  const { userId } = useAuth();
  const { users } = useUsers();
  const { entries } = useProjectActivityLog(measurement ? undefined : projectId, { limit: ACTIVITY_LOG_LIMIT });
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  const responsibleUser = users?.find(user => user.id === responsibleUserId);
  const responsibleName = responsibleUser ? presentUser(responsibleUser) : responsibleUserId;
  const isCurrentHolder = userId === responsibleUserId;
  const assignmentEvent = entries
    .filter(entry => isAssignmentEventForUser(entry, responsibleUserId))
    .sort(compareMostRecentFirst)
    .at(0);
  const whenCopy = getWhenCopy({
    artifactType,
    assignmentEvent,
    assignedAt: responsibleUserAssignedAt,
    isCurrentHolder,
    responsibleName,
  });
  const nextCopy = getNextCopy({
    artifactType,
    canApprove: canResponsibleUserApprove,
    isCurrentHolder,
    responsibleName,
  });
  const history = entries
    .filter(isResponsibilityHistoryEntry)
    .sort(compareMostRecentFirst)
    .slice(0, HISTORY_LIMIT);

  useEffect(() => {
    if (!open || measurement) return;

    const closeOnExternalPress = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnExternalPress);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnExternalPress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [measurement, open]);

  const handleButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={rootRef} css={ROOT_STYLE}>
      <LockIcon size={16} css={LOCK_STYLE} aria-hidden='true' />
      <button
        type='button'
        aria-controls={open && !measurement ? popoverId : undefined}
        aria-expanded={open && !measurement}
        aria-haspopup='dialog'
        css={BUTTON_STYLE(open && !measurement)}
        onClick={event => {
          event.stopPropagation();
          if (!measurement) setOpen(currentOpen => !currentOpen);
        }}
        onKeyDown={handleButtonKeyDown}
      >
        <span css={LABEL_STYLE}>{isCurrentHolder ? 'Your move' : `With ${getCompactPersonName(responsibleName)}`}</span>
        <span css={CARET_STYLE(open && !measurement)}>
          <CaretDownIcon size={9} aria-hidden='true' />
        </span>
      </button>
      {open && !measurement
        ? (
          <div id={popoverId} role='dialog' aria-label='Scope responsibility details' css={POPOVER_STYLE}>
            <p css={STATE_STYLE}>
              <LockIcon size={12} aria-hidden='true' />
              {getStageLabel(phase)}
            </p>
            <div css={DETAIL_LIST_STYLE}>
              <p css={DETAIL_ROW_STYLE}>
                <span css={DETAIL_TERM_STYLE}>Who:</span>The {artifactType} is with {isCurrentHolder ? 'you' : responsibleName}.
              </p>
              <p css={DETAIL_ROW_STYLE}>
                <span css={DETAIL_TERM_STYLE}>When:</span>
                {whenCopy}
              </p>
              <p css={DETAIL_ROW_STYLE}>
                <span css={DETAIL_TERM_STYLE}>What’s next:</span>
                {nextCopy}
              </p>
            </div>
            {history.length
              ? (
                <ul css={HISTORY_STYLE}>
                  {history.map(entry => (
                    <li key={entry.id} css={HISTORY_ENTRY_STYLE}>
                      <span css={HISTORY_ICON_STYLE}>
                        <CheckIcon size={10} aria-hidden='true' />
                      </span>
                      <span css={HISTORY_COPY_STYLE}>
                        <time css={HISTORY_DATE_STYLE} dateTime={new Date(entry.createdAt).toISOString()}>
                          {formatDate(entry.createdAt, 'MMM d, yyyy')}
                        </time>
                        {getHistoryCopy(entry)}
                      </span>
                    </li>
                  ))}
                </ul>
              )
              : null}
          </div>
        )
        : null}
    </div>
  );
};
